"use client";

// ============================================================
// CLUBHUB – SEPA-Lastschriftmandat erteilen (Baustein)
// ============================================================
// Derselbe Baustein im Aufnahmeantrag, in der Vorschau des
// Baukastens und im Mitgliederbereich („Bankverbindung aendern“):
// Kontoinhaber, IBAN mit sofortiger Pruefziffer, BIC freiwillig,
// der VOLLE Mandatstext mit Glaeubiger-ID und Mandatsreferenz, und
// die ausdrueckliche Bestaetigung. Nachweis ist das Absenden selbst
// (Zeitpunkt + Textfassung, `SubmissionMeta`) — ohne Unterschrift
// auf Papier.
// ============================================================
import { useId, useState } from "react";
import { CircleCheck, Landmark } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { mandatsText, type MandatsGlaeubiger } from "@/lib/formulare/einwilligungen";
import { ibanGueltig, ibanKompakt, ibanLesbar } from "@/lib/formulare/pruefen";
import type { SepaValue } from "@/lib/formulare/views";

export const SEPA_LEER: SepaValue = { holder: "", iban: "", bic: "", agreed: false };

export function SepaEingabe({
  value,
  onChange,
  error,
  glaeubiger,
  referenz,
  hinweisInhaber,
}: {
  value: SepaValue;
  onChange: (v: SepaValue) => void;
  /** Der Satz aus der Pruefung (`feldFehler`) — er gehoert an das erste Teilfeld, das fehlt. */
  error?: string;
  glaeubiger: MandatsGlaeubiger;
  referenz: string | null;
  hinweisInhaber?: string;
}) {
  const id = useId();
  const [ibanBeruehrt, setIbanBeruehrt] = useState(false);
  const kompakt = ibanKompakt(value.iban);
  const gueltig = ibanGueltig(kompakt);
  const zuKurz = kompakt.length < 15;
  const ibanFehler =
    (error && /IBAN/.test(error) ? error : undefined) ??
    (ibanBeruehrt && kompakt.length > 0 && !gueltig ? (zuKurz ? "Die IBAN ist noch nicht vollständig." : "Diese IBAN stimmt nicht — bitte die Ziffern prüfen.") : undefined);
  const inhaberFehler = error && /Kontoinhaber/.test(error) ? error : undefined;
  const bestaetigFehler = error && /bestätigen/.test(error) ? error : undefined;

  return (
    <div className="border-border bg-bg-primary space-y-4 rounded-xl border p-4">
      <div className="flex items-center gap-2">
        <Landmark className="text-text-tertiary h-4 w-4" aria-hidden />
        <p className="text-body-md text-text-primary font-medium">SEPA-Lastschriftmandat</p>
      </div>
      <Input
        id={`${id}-inhaber`}
        label="Kontoinhaber"
        value={value.holder}
        onChange={(e) => onChange({ ...value, holder: e.target.value })}
        error={inhaberFehler}
        hint={hinweisInhaber ?? "So, wie der Name bei der Bank steht."}
        autoComplete="cc-name"
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_10rem]">
        <div className="space-y-1.5">
          <Input
            id={`${id}-iban`}
            label="IBAN"
            value={value.iban}
            onChange={(e) => onChange({ ...value, iban: e.target.value.toUpperCase() })}
            onBlur={() => {
              setIbanBeruehrt(true);
              if (kompakt) onChange({ ...value, iban: ibanLesbar(kompakt) });
            }}
            error={ibanFehler}
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="DE00 0000 0000 0000 0000 00"
            className="font-mono tracking-wide"
          />
          {gueltig && !ibanFehler && (
            <p className="text-caption text-success flex items-center gap-1.5">
              <CircleCheck className="h-3.5 w-3.5" aria-hidden />
              Prüfziffer stimmt
            </p>
          )}
        </div>
        <Input
          id={`${id}-bic`}
          label="BIC (freiwillig)"
          value={value.bic}
          onChange={(e) => onChange({ ...value, bic: e.target.value.toUpperCase() })}
          autoComplete="off"
          spellCheck={false}
          className="font-mono"
        />
      </div>
      <div className="bg-bg-secondary text-caption text-text-secondary max-h-48 space-y-2 overflow-y-auto rounded-lg px-3 py-2.5" tabIndex={0} aria-label="Mandatstext">
        {mandatsText(glaeubiger, referenz).map((absatz, i) => (
          <p key={i} className={cn(i < 3 && "text-text-primary tabular-nums")}>
            {absatz}
          </p>
        ))}
      </div>
      <div className="space-y-1.5">
        <Checkbox
          id={`${id}-ok`}
          checked={value.agreed}
          onChange={(e) => onChange({ ...value, agreed: e.target.checked })}
          label="Ich erteile dieses SEPA-Lastschriftmandat."
          hint="Gilt als Unterschrift: gespeichert werden Zeitpunkt und diese Fassung des Texts."
        />
        {bestaetigFehler && (
          <p className="text-caption text-danger pl-6" role="alert">
            {bestaetigFehler}
          </p>
        )}
      </div>
    </div>
  );
}
